import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import "./ProviderFacilityField.scss";

/** The specialist-owned provider/facility/department values for a Quick Entry session (Feature 3, AB#34099). */
export interface ProviderFacilityValues {
    readonly providerName: string;
    readonly facilityName: string;
    /**
     * PRD v2.10 AC6 (line 308): all three values are editable, NOT read-only. Line 309 allows
     * deferring department STORAGE if the schema work cannot land, but never its editability -
     * "extraction and display remain". Storage did land (DataName 160041), so department is a
     * normal editable, persisted field rather than the read-only span it used to be.
     */
    readonly department: string;
}

export interface ProviderFacilityFieldProps {
    readonly values: ProviderFacilityValues;
    readonly onChange: (values: ProviderFacilityValues) => void;
    /**
     * Two or more competing records-subjects were detected. Per AC9 (line 311), F3 passes NO single
     * scope value downstream in that case - the specialist decides - so the inputs start blank and
     * the notice explains why.
     */
    readonly multipleSubjects?: boolean;
    /**
     * No scoped records-subject: a general release, an uncertain extraction, or F3 flag-gated off.
     * Renders the PRD line 136 "No provider specified" state instead of a blank row.
     */
    readonly noProviderSpecified?: boolean;
    /**
     * F3 is enabled (its feature flag is on), so the specialist may enter or correct values. When
     * false the group renders the "No provider specified" state ALONE, with no inputs: F3 is not
     * capturing anything yet, and an editable field whose value is discarded on reopen is worse
     * than no field at all.
     */
    readonly allowEntry?: boolean;
}

export const PROVIDER_FACILITY_ROW_ID = "providerFacility";

export const MULTIPLE_SUBJECTS_MESSAGE =
    "Multiple records-subjects detected. Enter the provider or facility this request is scoped to, or leave blank.";

export const NO_PROVIDER_SPECIFIED_MESSAGE = "No provider specified";

/**
 * Provider / Facility / Department Quick Entry row (Spec #34099 / F3).
 *
 * The confirmed values are the downstream input for QA AI 1.2 record-scope validation. The base
 * (title-stripped) provider name is derived server-side at commit from whatever is entered here, so
 * this component keeps the human-readable form and nothing else.
 *
 * ON THE UNSCOPED STATE - reconciling two ACs that pull in opposite directions:
 *   - AC4 (PRD v2.10 line 306) says a general release shows NO row.
 *   - The Feature 1 AC (line 136) says Quick Entry must show a "No provider specified" state when
 *     F3 returns nothing OR is flag-gated off, "rather than an empty or broken row".
 * We render the group with an explicit "No provider specified" label and a Neutral status (no
 * exception badge). That satisfies line 136 - the three states (general release, uncertain
 * extraction, F3 disabled) stop being indistinguishable blank rows - while honouring AC4's intent:
 * no scope constraint, and nothing demanding specialist action on the majority of transactions.
 *
 * It also restores the precision-first escape hatch (line 319): the PRD justifies treating
 * uncertain cases as unscoped because "the specialist can add a provider/facility manually if one
 * is warranted". With the row hidden there was no input to type into, so every false negative was
 * permanent and invisible.
 */
const ProviderFacilityField: React.FC<ProviderFacilityFieldProps> = (props) => {
    const onFieldChange = (field: keyof ProviderFacilityValues, value: string) => {
        props.onChange({ ...props.values, [field]: value });
    };

    return (
        <div className="provider-facility-field">
            {props.multipleSubjects ? (
                <div className="provider-facility-field__multiple" role="status">
                    {MULTIPLE_SUBJECTS_MESSAGE}
                </div>
            ) : null}
            {props.noProviderSpecified && !props.multipleSubjects ? (
                <div className="provider-facility-field__none" role="status">
                    {props.allowEntry
                        ? `${NO_PROVIDER_SPECIFIED_MESSAGE} — add one only if this request is scoped to a specific provider, facility or department.`
                        : NO_PROVIDER_SPECIFIED_MESSAGE}
                </div>
            ) : null}
            {props.allowEntry === false ? null : (
            <>
            <label className="provider-facility-field__field">
                <span className="provider-facility-field__label">Provider</span>
                <input
                    type="text"
                    className="form-control provider-facility-field__input"
                    data-field="providerName"
                    maxLength={200}
                    placeholder="Provider name"
                    value={props.values.providerName}
                    onChange={event => onFieldChange("providerName", event.target.value)}
                />
            </label>
            <label className="provider-facility-field__field">
                <span className="provider-facility-field__label">Facility</span>
                <input
                    type="text"
                    className="form-control provider-facility-field__input"
                    data-field="facilityName"
                    maxLength={200}
                    placeholder="Facility name"
                    value={props.values.facilityName}
                    onChange={event => onFieldChange("facilityName", event.target.value)}
                />
            </label>
            <label className="provider-facility-field__field">
                <span className="provider-facility-field__label">Department</span>
                <input
                    type="text"
                    className="form-control provider-facility-field__input"
                    data-field="department"
                    maxLength={200}
                    placeholder="Department"
                    value={props.values.department}
                    onChange={event => onFieldChange("department", event.target.value)}
                />
            </label>
            </>
            )}
        </div>
    );
};

export default ProviderFacilityField;

/**
 * Builds the Provider/Facility FieldGroupRow. Amber (LowConfidence) on multiple subjects - the
 * specialist has to resolve it. Otherwise Confirmed once a value is present, and EmptyNotRequired
 * when nothing is scoped: an unscoped request is a complete and valid answer, so it must NOT render
 * as NeedsEntry - that would push "No provider specified" into the exception count on the majority
 * of transactions and block Confirm All. EmptyNotRequired is the existing tier for exactly this
 * (empty + not required, sort tier 2, below every real exception).
 *
 * `aiValues` are what the extraction produced, so the row can report whether it still shows the AI
 * values or a specialist edit.
 */
export function mapProviderFacilityToFieldGroupRow(
    badgeNumber: number,
    values: ProviderFacilityValues,
    options?: {
        readonly multipleSubjects?: boolean;
        readonly aiProviderName?: string;
        readonly aiFacilityName?: string;
        readonly aiDepartment?: string;
    },
): FieldGroupRow {
    const provider = values.providerName.trim();
    const facility = values.facilityName.trim();
    const department = values.department.trim();
    const parts = [provider, facility, department].filter(Boolean);
    const hasValue = parts.length > 0;

    const isAiPreSelected = hasValue
        && provider === (options?.aiProviderName ?? "").trim()
        && facility === (options?.aiFacilityName ?? "").trim()
        && department === (options?.aiDepartment ?? "").trim();

    return {
        id: PROVIDER_FACILITY_ROW_ID,
        badgeNumber,
        status: options?.multipleSubjects
            ? FieldGroupRowStatus.LowConfidence
            : hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.EmptyNotRequired,
        label: "Provider / Facility",
        value: options?.multipleSubjects
            ? MULTIPLE_SUBJECTS_MESSAGE
            : hasValue ? parts.join(" · ") : NO_PROVIDER_SPECIFIED_MESSAGE,
        isAiPreSelected: Boolean(isAiPreSelected) && !options?.multipleSubjects,
    };
}
